import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type {
	IProduct,
	IProductComplianceTechnical,
	IProductComplianceTechnicalMedia,
	ITheme
} from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { Image } from '@inter-ikea-kompis/component-image';

/**
 * Product Card Water Sense Image Component.
 */

export default class ProductCardWaterSenseImage extends KompisElement {
	@property({ type: Object, required: true })
	public theme!: ITheme;

	@property({ type: Object, required: true })
	public product!: IProduct;

	/**
	 * Renders a water-senses compliance image.
	 */
	public override render(): RenderResult {
		const imageElement = this.getImageElement();
		return imageElement;
	}

	public override connectedCallback(): void {
		this.customElements.define('kompis-image', Image);

		super.connectedCallback();
	}

	/**
	 * Returns a kompis-image element or null.
	 */
	private getImageElement(): RenderResult {
		const complianceTechnicalWaterSense = this.findProductComplianceTechnicalWaterSense();
		const src = this.getImageSrc(complianceTechnicalWaterSense);
		const alt = this.getImageAlt(complianceTechnicalWaterSense);

		if (src) {
			return html`
				<style>
					:host {
						display: flex;
					}

					kompis-image {
						width: ${64 / 16}rem;
						height: auto;
						margin-top: ${8 / 16}rem;
						margin-bottom: ${8 / 16}rem;
					}
				</style>
				<kompis-image .theme="${this.theme}" .alt="${alt}" .src="${src}"></kompis-image>
			`;
		}
		return null;
	}
	/**
	 * Returns a image alt text.
	 *
	 * @param complianceTechnicalWaterSense ...
	 * @returns Alt.
	 */
	private getImageAlt(
		complianceTechnicalWaterSense: IProductComplianceTechnical | null
	): string | null {
		if (complianceTechnicalWaterSense) {
			const { headingText, text } = complianceTechnicalWaterSense;
			return `${headingText} ${text}`;
		}
		return null;
	}

	/**
	 * Returns a image src url.
	 *
	 * @param complianceTechnicalWaterSense ...
	 * @returns Src.
	 */
	private getImageSrc(
		complianceTechnicalWaterSense: IProductComplianceTechnical | null
	): string | null {
		const mediaItem = this.getMediaItem(complianceTechnicalWaterSense);
		return mediaItem?.url ?? null;
	}

	/**
	 * Returns a single media item from the complianceTechnical media array.
	 *
	 * @param complianceTechnicalWaterSense ...
	 * @returns IProductComplianceTechnicalMedia.
	 */
	private getMediaItem(
		complianceTechnicalWaterSense: IProductComplianceTechnical | null
	): IProductComplianceTechnicalMedia | null {
		const svgMedia = this.findMediaItem(complianceTechnicalWaterSense, '00017');
		const jpegMedia = this.findMediaItem(complianceTechnicalWaterSense, '00001');
		return svgMedia ?? jpegMedia;
	}

	/**
	 * Function for finding a media that has a svg fileTypeCode.
	 *
	 * @param complianceTechnicalWaterSense ...
	 * @param fileTypeCode ...
	 * @returns IProductComplianceTechnicalMedia.
	 */
	private findMediaItem(
		complianceTechnicalWaterSense: IProductComplianceTechnical | null,
		fileTypeCode: string
	): IProductComplianceTechnicalMedia | null {
		if (complianceTechnicalWaterSense) {
			const { media } = complianceTechnicalWaterSense;
			return media?.find((item) => item.fileTypeCode === fileTypeCode) ?? null;
		}

		return null;
	}

	/**
	 * Returns a compliance technical information that is of type Water Sense.
	 *
	 * @returns IProductComplianceTechnicalMedia.
	 */
	private findProductComplianceTechnicalWaterSense(): IProductComplianceTechnical | null {
		const complianceTechnical = this.product?.content?.complianceTechnical;
		return complianceTechnical?.find(this.isWaterSenseItem) ?? null;
	}

	/**
	 * Check if item in complianceTechnical has a headingType WATER_SENSE and media key with value.
	 *
	 * @param complianceTechnical ...
	 * @returns Boolean.
	 */
	private isWaterSenseItem(complianceTechnical: IProductComplianceTechnical): boolean {
		const hasHeadingType = complianceTechnical.headingType === 'WATER_SENSE';
		const hasMedia = 'media' in complianceTechnical;

		return hasHeadingType && hasMedia;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-product-card-water-sense-image': ProductCardWaterSenseImage;
	}
}
